import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import BaseDrawer from '../templates/BaseDrawer';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';

export default function StoreSelectorModal({
  open,
  onClose,
  // Redux state (via container)
  chains,
  storeNames,
  categories,
  loading,
  currentChain,
  currentStoreName,
  currentStoreIndex,
  currentCategory,
  currentKey,
  categoryProgressLoading,
  // Redux actions (via container)
  setChain,
  getStoreNames,
  setStoreName,
  setStore,
  setCategory,
  resetCategory,
  getTasksProgress,
}) {
  const { t } = useTranslation();
  // Local draft — changes are staged here until the user hits Accept
  const [staged, setStaged] = useState(null);
  const [subView, setSubView] = useState(null); // null | 'chain' | 'store' | 'category'
  const [search, setSearch] = useState('');
  // Snapshot of the original category selection — restored if the user comes back to the original store
  const [savedCategory, setSavedCategory] = useState(null);

  // Falls back to Redux values when no draft exists
  const draft = staged ?? {
    chain: currentChain,
    storeName: currentStoreName,
    storeIndex: currentStoreIndex,
    categoryName: currentCategory,
    categoryKey: currentKey,
  };

  const storeChanged = draft.storeIndex !== currentStoreIndex;

  const handleClose = () => {
    setSubView(null);
    setSearch('');
    setStaged(null);
    setSavedCategory(null);
    onClose();
  };

  const openSubView = (view) => {
    setSearch('');
    setSubView(view);
  };

  // Auto-select first store when chain changes
  const handleChainSelect = (chain) => {
    getStoreNames(chain);
    const first = (chains[chain] || [])[0];
    const firstTitle = first ? `${first.store} - ${first.name}` : '';
    if (!savedCategory) {
      setSavedCategory({ categoryName: draft.categoryName, categoryKey: draft.categoryKey });
    }
    setStaged({ ...draft, chain, storeName: firstTitle, storeIndex: first?.index ?? 0, categoryName: null, categoryKey: null });
    setSubView(null);
  };

  const handleStoreSelect = (item) => {
    const returningToOriginal = item.index === currentStoreIndex;
    if (returningToOriginal && savedCategory) {
      setStaged({ ...draft, storeName: item.title, storeIndex: item.index, ...savedCategory });
      setSavedCategory(null);
    } else {
      if (!savedCategory) {
        setSavedCategory({ categoryName: draft.categoryName, categoryKey: draft.categoryKey });
      }
      setStaged({ ...draft, storeName: item.title, storeIndex: item.index, categoryName: null, categoryKey: null });
    }
    setSubView(null);
  };

  const handleCategorySelect = (cat) => {
    setStaged({ ...draft, categoryName: cat.name, categoryKey: cat.value });
    setSubView(null);
  };

  // Apply draft to Redux — only place where Redux gets updated
  const handleApply = () => {
    setChain(draft.chain);
    setStoreName(draft.storeName);
    setStore(draft.storeIndex);
    if (!storeChanged && draft.categoryKey !== null) {
      setCategory(draft.categoryName, draft.categoryKey);
    } else if (!storeChanged && draft.categoryKey === null) {
      resetCategory();
      getTasksProgress();
    } else {
      resetCategory();
    }
    handleClose();
  };

  const chainKeys = Object.keys(chains || {});

  const filteredStores = storeNames.filter((s) =>
    s.title?.toLowerCase().includes(search.toLowerCase()),
  );

  const filteredCategories = categories.filter((c) =>
    c.name && c.name.toLowerCase().includes(search.toLowerCase()),
  );

  const subTitle = {
    chain: t('bruno.store.chain', 'Chain'),
    store: t('bruno.store.store', 'Store'),
    category: t('bruno.store.category', 'Category'),
  };

  return (
    <BaseDrawer open={open} onClose={handleClose} height="75vh" maxWidth={600}>
      {/* Header — shows back arrow when inside a sub-view, close button on main view */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 2, pb: 1, flexShrink: 0 }}>
        {subView ? (
          <IconButton onClick={() => setSubView(null)} size="small" sx={{ mr: 1 }}>
            <ChevronLeftIcon />
          </IconButton>
        ) : null}
        <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
          {subView ? subTitle[subView] : t('bruno.store.select_store', 'Select store')}
        </Typography>
        {!subView && (
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2, bgcolor: 'background.paper' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : subView === null ? (
          // Main view — 3 rows: chain, store, category
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton onClick={() => openSubView('chain')}>
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                  <LayersOutlinedIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
                </Box>
                <ListItemText
                  primary={t('bruno.store.chain', 'Chain')}
                  secondary={draft.chain || '—'}
                />
                <ChevronRightIcon sx={{ color: 'text.secondary' }} />
              </ListItemButton>
            </ListItem>

            <Divider component="li" />

            {/* Store row disabled until a chain is selected */}
            <ListItem disablePadding>
              <ListItemButton onClick={() => openSubView('store')} disabled={!draft.chain}>
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                  <StorefrontOutlinedIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
                </Box>
                <ListItemText
                  primary={t('bruno.store.store', 'Store')}
                  secondary={draft.storeName || '—'}
                />
                <ChevronRightIcon sx={{ color: 'text.secondary' }} />
              </ListItemButton>
            </ListItem>

            <Divider component="li" />

            <ListItem disablePadding>
              <ListItemButton onClick={() => openSubView('category')} disabled={!draft.chain || categoryProgressLoading || storeChanged}>
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                  <CategoryOutlinedIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
                </Box>
                <ListItemText
                  primary={t('bruno.store.category', 'Category')}
                  secondary={
                    // categoryKey === null means "All" was selected
                    draft.categoryKey === null
                      ? t('bruno.store.all_name', 'All')
                      : draft.categoryName || '—'
                  }
                />
                <ChevronRightIcon sx={{ color: 'text.secondary' }} />
              </ListItemButton>
            </ListItem>
          </List>
        ) : (
          // Sub-selector: searchable list for store or category
          <>
            {(subView === 'store' || subView === 'category') && (
              <TextField
                fullWidth
                size="small"
                placeholder={
                  subView === 'store'
                    ? t('bruno.store.search_store', 'Search store...')
                    : t('bruno.store.search_category', 'Search category...')
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  my: 1.5,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    '& fieldset': { borderColor: 'divider' },
                    '&:hover fieldset': { borderColor: 'text.disabled' },
                    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                  },
                }}
              />
            )}

            <List disablePadding>
              {subView === 'chain' &&
                chainKeys.map((chain) => (
                  <ListItem key={chain} disablePadding>
                    <ListItemButton
                      selected={chain === draft.chain}
                      onClick={() => handleChainSelect(chain)}
                    >
                      <ListItemText primary={chain} />
                    </ListItemButton>
                  </ListItem>
                ))}

              {subView === 'store' &&
                filteredStores.map((item) => (
                  <ListItem key={item.index} disablePadding>
                    <ListItemButton
                      selected={item.index === draft.storeIndex}
                      onClick={() => handleStoreSelect(item)}
                    >
                      <ListItemText primary={item.title} />
                    </ListItemButton>
                  </ListItem>
                ))}

              {subView === 'category' &&
                filteredCategories.map((cat) => (
                  <ListItem key={cat.value} disablePadding>
                    <ListItemButton
                      selected={cat.value === draft.categoryKey}
                      onClick={() => handleCategorySelect(cat)}
                    >
                      <ListItemText
                        primary={cat.value === null ? t('bruno.store.all_name', 'All') : cat.name}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          </>
        )}
      </Box>

      {/* Accept / Cancel — only shown on main view */}
      {!subView && (
        <Box sx={{ px: 2, pb: 5, pt: 1, flexShrink: 0, display: 'flex', flexDirection: 'row', gap: 1, bgcolor: 'background.paper' }}>
          <Button variant="outlined" fullWidth onClick={handleClose}>
            {t('bruno.buttons.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" fullWidth onClick={handleApply} disabled={!draft.chain || !draft.storeName}>
            {t('bruno.buttons.accept', 'Accept')}
          </Button>
        </Box>
      )}
    </BaseDrawer>
  );
}
